import React from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

const SCOPE_COPY = {
  chat: {
    loadingSub: 'Reading administrator chat settings',
    connectedSub: 'SAP context enabled by your organization',
    disconnectedSub: 'No SAP context — set by your administrator',
    connectedTitle: 'Connected — SAP context enabled for this organization',
    disconnectedTitle: 'Disconnected — ABAP-only mode',
    loadingTitle: 'Loading organization chat mode…',
  },
  ticket: {
    loadingSub: 'Reading administrator ticket settings',
    connectedSub: 'SAP discovery and validation enabled for tickets',
    disconnectedSub: 'Lightweight ticket processing — no SAP context',
    connectedTitle: 'Connected — SAP context enabled for ticket workflows',
    disconnectedTitle: 'Disconnected — lightweight ticket mode',
    loadingTitle: 'Loading organization ticket mode…',
  },
};

/**
 * Connected / disconnected indicator matching Vibe Coding Studio chat UX.
 * @param {{ connected: boolean | null, scope?: 'chat' | 'ticket', className?: string, testId?: string }} props
 */
export function PlatformConnectionStatus({ connected, scope = 'chat', className, testId }) {
  const ready = connected !== null;
  const isConnected = connected === true;
  const copy = SCOPE_COPY[scope] || SCOPE_COPY.chat;

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border border-cp-line/80 bg-cp-surface px-3 py-2 shadow-sm',
        className
      )}
      data-testid={testId || `platform-connection-status-${scope}`}
      title={
        !ready ? copy.loadingTitle : isConnected ? copy.connectedTitle : copy.disconnectedTitle
      }
    >
      <span className="relative flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        {!ready ? (
          <Loader2 className="h-3 w-3 animate-spin text-cp-faint" aria-hidden />
        ) : isConnected ? (
          <>
            <span
              className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cp-ok opacity-40"
              aria-hidden
            />
            <span
              className="relative inline-flex h-3 w-3 rounded-full bg-cp-ok shadow-[0_0_10px_2px_rgba(16,185,129,0.65)]"
              aria-hidden
            />
          </>
        ) : (
          <span
            className="inline-flex h-3 w-3 rounded-full border border-cp-line bg-cp-surface2"
            aria-hidden
          />
        )}
      </span>
      <div className="text-xs leading-snug text-cp-muted">
        <span className="font-medium">
          {!ready ? 'Checking mode…' : isConnected ? 'Connected' : 'Disconnected'}
        </span>
        <span className="block text-[11px] font-normal text-muted-foreground">
          {!ready ? copy.loadingSub : isConnected ? copy.connectedSub : copy.disconnectedSub}
        </span>
      </div>
    </div>
  );
}
